/**
 * Cliente del CRUD de artifact_comments.
 * Backend: app/api/v1/comments.py · Doc 21 — Fases 4 y 5.
 *
 * Anclaje: `(artifact_version_id, review_cycle, section_id,
 * instance_key)`. El `review_cycle` discrimina intentos de submit
 * dentro de la misma versión (ver Doc 07 §"Modelo de revisión y
 * numeración").
 *
 * Reglas de permisos (server-enforced):
 *   - Crear root/reply: cualquier project_member del proyecto.
 *   - Editar body de root/reply: solo el author.
 *   - Borrar: author OR PM (`rol-pm-internal`) del proyecto.
 *   - Status transitions:
 *       · pending → addressed: artifact author (creador del draft activo).
 *       · addressed → resolved: commenter (author del root).
 *       · pending → resolved direct: commenter o PM.
 *       · cualquier transición forzada: PM (audit kept).
 *   - Threads están limitados a 1 nivel: una reply NO puede tener
 *     replies (server responde 422 COMMENT_INVALID_PARENT).
 */

import { apiFetch, apiStream, apiStreamResult, type SseEvent } from './client'
import type { GroundingReport } from './citations'

export type CommentKind = 'comment' | 'reply' | 'status_change'
export type CommentStatus = 'pending' | 'addressed' | 'resolved'

export interface ArtifactComment {
  id: string
  artifact_version_id: string
  /** Doc 21 Fase 6.b — populado solo por endpoints "historial completo"
   *  (`/all-comments`) que agregan comments de TODAS las versions del
   *  artifact. El frontend usa este campo para agrupar visualmente la
   *  vista de historial. Endpoints version-scoped lo dejan null. */
  version_number: number | null
  review_cycle: number
  section_id: string
  instance_key: string
  /** Null si el author fue eliminado del tenant (FK SET NULL). */
  author_user_id: string | null
  body: string
  /** Snapshot del texto seleccionado por el reviewer al crear el
   *  comment. Null si comentó a nivel sección sin selección. */
  excerpt: string | null
  /** Doc 21 Fase 6 — snapshot del valor COMPLETO de la sección anclada
   *  `(section_id, instance_key)` al momento de crear el comment.
   *  Frozen forever; el card lo renderiza en el bloque "Versión previa"
   *  para que el reviewer compare "qué decía la sección entonces" vs
   *  el contenido vivo del documento abierto. Null en comments creados
   *  antes de Fase 6 o cuando la sección no existía en el content. El
   *  shape es heterogéneo: TipTap doc para rich_text, array para
   *  repeatables, string para plain_text, etc. */
  section_snapshot: unknown
  /** Doc 21 D5 — discrimina root, reply de texto, y eventos
   *  system de cambio de status. */
  kind: CommentKind
  /** Para `kind='status_change'` lleva `{from, to, note?}`. Reservado
   *  para `system_origin` de IA en Fase CR. */
  system_payload: Record<string, unknown> | null
  parent_comment_id: string | null
  status: CommentStatus
  status_changed_at: string | null
  status_changed_by_user_id: string | null
  created_at: string
  updated_at: string
}

export interface CreateCommentBody {
  section_id: string
  /** Para secciones repetibles (`casos_de_uso[0].descripcion`). Si
   *  null, el server lo normaliza a `section_id`. */
  instance_key?: string | null
  body: string
  /** Texto seleccionado por el reviewer (capturado vía
   *  `window.getSelection()`). Max 500 chars. */
  excerpt?: string | null
}

export interface CreateReplyBody {
  body: string
}

export interface UpdateCommentBody {
  body: string
}

export interface UpdateCommentStatusBody {
  status: CommentStatus
  /** Mensaje opcional que acompaña la transición. Visible en el thread
   *  como nota del evento (ej. "Subsanado en sección 3.2"). */
  note?: string | null
}

/** Lista todos los comentarios de una versión, ordenados por
 *  section_id → instance_key → created_at asc. */
export async function listVersionComments(
  accessToken: string,
  artifactId: string,
  versionId: string,
): Promise<ArtifactComment[]> {
  return apiFetch<ArtifactComment[]>(
    `/api/v1/artifacts/${artifactId}/versions/${versionId}/comments`,
    { accessToken },
  )
}

/** Crea un comentario inline. El actor debe ser member del proyecto
 *  del artifact (server-enforced — 403 COMMENT_NOT_PROJECT_MEMBER si
 *  no lo es). */
export async function createComment(
  accessToken: string,
  artifactId: string,
  versionId: string,
  body: CreateCommentBody,
): Promise<ArtifactComment> {
  return apiFetch<ArtifactComment>(
    `/api/v1/artifacts/${artifactId}/versions/${versionId}/comments`,
    {
      accessToken,
      method: 'POST',
      body: body as unknown as Record<string, unknown>,
    },
  )
}

/** Edita el body de un comentario. Solo el author puede; otros
 *  miembros reciben 403 COMMENT_NOT_AUTHOR. */
export async function updateComment(
  accessToken: string,
  commentId: string,
  body: UpdateCommentBody,
): Promise<ArtifactComment> {
  return apiFetch<ArtifactComment>(`/api/v1/comments/${commentId}`, {
    accessToken,
    method: 'PATCH',
    body: body as unknown as Record<string, unknown>,
  })
}

/** Borra un comentario. Permitido para author o cualquier PM del
 *  proyecto. Server responde 204 (sin body). */
export async function deleteComment(
  accessToken: string,
  commentId: string,
): Promise<void> {
  return apiFetch<void>(`/api/v1/comments/${commentId}`, {
    accessToken,
    method: 'DELETE',
  })
}

// ─── Fase 5 — threads + status workflow + v−1 filter ───────────────────────

/** Doc 21 Fase 5 — comments del cycle inmediatamente anterior con
 *  status `pending|addressed`. Lo consume el editor (panel lateral) +
 *  el submit gate. Si el cycle activo es 0 (nunca sometido) o el
 *  artifact no tiene v−1, devuelve [] limpio. */
export async function listActiveComments(
  accessToken: string,
  artifactId: string,
  options?: { versionId?: string | null },
): Promise<ArtifactComment[]> {
  const qs = options?.versionId ? `?version_id=${options.versionId}` : ''
  return apiFetch<ArtifactComment[]>(
    `/api/v1/artifacts/${artifactId}/active-comments${qs}`,
    { accessToken },
  )
}

/** Doc 21 Fase 6.b — historial completo: TODOS los comments de TODAS
 *  las versions del artifact, incluyendo resolved. Cada item trae
 *  `version_number` para que el caller pueda agrupar por versión sin
 *  queries adicionales. Orden: version DESC + cycle DESC + fecha ASC. */
export async function listAllComments(
  accessToken: string,
  artifactId: string,
): Promise<ArtifactComment[]> {
  return apiFetch<ArtifactComment[]>(
    `/api/v1/artifacts/${artifactId}/all-comments`,
    { accessToken },
  )
}

/** Crea una reply de texto bajo un root. El server hereda el anchor
 *  (version, review_cycle, section, instance) del parent y rechaza
 *  con 409 COMMENT_ROOT_RESOLVED si el root ya está cerrado, o 422
 *  COMMENT_INVALID_PARENT si el parent_id no es un root. */
export async function createReply(
  accessToken: string,
  commentId: string,
  body: CreateReplyBody,
): Promise<ArtifactComment> {
  return apiFetch<ArtifactComment>(
    `/api/v1/comments/${commentId}/replies`,
    {
      accessToken,
      method: 'POST',
      body: body as unknown as Record<string, unknown>,
    },
  )
}

/** Transiciona el status de un root comment con permisos por rol.
 *  Server responde 403 COMMENT_INVALID_STATUS_TRANSITION si el rol del
 *  caller no puede ejecutar la transición. */
export async function updateCommentStatus(
  accessToken: string,
  commentId: string,
  body: UpdateCommentStatusBody,
): Promise<ArtifactComment> {
  return apiFetch<ArtifactComment>(
    `/api/v1/comments/${commentId}/status`,
    {
      accessToken,
      method: 'PATCH',
      body: body as unknown as Record<string, unknown>,
    },
  )
}

// ─── Fase 7 — IA co-reviewer ────────────────────────────────────────────────

/** Doc 21 Fase 7.g Paso 1 — un step del tool loop que ejecutó el agente.
 *  El modal lo muestra para que el user vea qué consultó la IA. */
export interface CommentAiToolTraceEntry {
  iteration: number
  tool: string
  args: Record<string, unknown>
  result_chars: number
}

/** Doc 21 Fase 7 — sugerencia IA persistida (`comment_ai_responses`).
 *
 * El backend OCULTA `verdict` en MVP por decisión UX (se persiste pero
 * no se devuelve). Si en futuras fases se expone, agregar el field aquí
 * y wire-uppear en la UI. */
export interface CommentAiSuggestion {
  id: string
  comment_id: string
  suggested_text: string
  /** Doc 24 rework — JSON canónico tipado del nuevo contenido. Su forma
   *  depende del `section_type`:
   *    · rich_text / plain_text       → string
   *    · bullet_list / numbered_list  → string[]
   *    · checklist                    → { text: string; done: boolean }[]
   *    · key_value_grid               → { key: string; value: string }[]
   *    · structured_table             → Record<string, unknown>[]
   *    · structured                   → unknown (per schema)
   *    · code_block                   → { language: string; code: string }
   *    · mermaid_*                    → { code: string }
   *  El editor lo aplica DIRECTO al state de la sección sin parser
   *  intermedio de markdown. `null` solo para rows legacy pre-rework
   *  (en ese caso el frontend cae al parser sobre `suggested_text`). */
  suggested_value: unknown | null
  /** Doc 24 rework — snapshot del current_text serializado en el
   *  backend con las mismas columnas/labels que `suggested_text`.
   *  El modal "Subsanar con IA" lo usa como `originalText` del diff
   *  cuando viene poblado, garantizando comparación apples-to-apples
   *  (estructuras como structured_table v2 antes salían vacías del
   *  lado izquierdo del diff porque el helper del frontend no
   *  conocía las columnas del template). `null` para rows legacy
   *  pre-rework — en ese caso el frontend cae al fallback
   *  `valueToReadableText(comment.section_snapshot)`. */
  section_current_text: string | null
  /** OT-0049 — juicio por oración de la sugerencia: oraciones sin respaldo
   *  que el autor ve junto al diff antes de aplicar. `null` en filas
   *  anteriores a la OT o si la sección no pasa por el juez. */
  grounding: GroundingReport | null
  reasoning: string | null
  refinement_depth: number
  /** Null = primera petición. Si != null, este row es un refinement del
   *  apuntado. La cadena se enforce: depth incrementa y cap=3 server-side. */
  previous_response_id: string | null
  /**
   * @deprecated Doc 25 §F2.5 — backend dropeó la columna BD. El
   * endpoint mantiene el campo (default `""`) pero la fuente de verdad
   * vive en `llm_usage_records` joinable por `request_id`. Usar
   * `useUsageRecordByRequest(request_id)` desde el frontend. Este
   * campo se eliminará del contrato una vez F3+F4 estén estables.
   */
  model_id: string
  /** @deprecated Ver `model_id` arriba. Usar `useUsageRecordByRequest`. */
  tokens_input: number
  /** @deprecated Ver `model_id` arriba. Usar `useUsageRecordByRequest`. */
  tokens_output: number
  /** @deprecated Ver `model_id` arriba. Usar `useUsageRecordByRequest`. */
  latency_ms: number
  /**
   * Doc 25 §4.4 Migración D — request_id correlaciona esta sugerencia
   * con `llm_usage_records`. Pasar a `useUsageRecordByRequest` para
   * obtener tokens/latency/model_id reales.
   */
  request_id: string | null
  /** Null = no aplicada (descartada o pendiente). Cuando el user clickea
   *  "Aplicar" en el modal, se persiste `applied_at` + `applied_by` —
   *  pero NO modifica `artifact_versions`; el editor del frontend inserta
   *  `suggested_text` en el draft activo. */
  applied_at: string | null
  /** Doc 21 Fase 7.g — Cancelar UX. Si != null, el user clickeó
   *  "Cancelar" en el modal y descartó esta propuesta. El modal NO
   *  hace auto-resume sobre rows con `discarded_at != null` al
   *  reabrir. La row PERMANECE en BD para audit + token accounting. */
  discarded_at: string | null
  created_at: string
  /** Doc 21 Fase 7.g Paso 1 — tools que invocó el agente:
   *  · null  → row pre-Fase 7.g (sin tracking).
   *  · []    → el agente respondió sin invocar tools (caso simple).
   *  · [...] → el agente consultó knowledge base o leyó otras secciones. */
  tool_trace: CommentAiToolTraceEntry[] | null
}

export interface CreateAiSuggestionBody {
  /** Hint extra del autor ("hazlo más corto"). Max 1000 chars
   *  server-side. */
  user_instructions?: string | null
  /** Para refinement: id de la response anterior. Server valida que
   *  pertenezca al mismo comment y que la cadena no haya alcanzado
   *  el cap de 3 (depth max = 3). */
  previous_response_id?: string | null
}

/** Lista el historial de invocaciones del agente para un comment.
 *  Útil cuando el modal se reabre — el caller reconstruye el estado del
 *  refinement loop sin perderlo. Server devuelve orden cronológico ASC. */
export async function listCommentAiSuggestions(
  accessToken: string,
  commentId: string,
): Promise<CommentAiSuggestion[]> {
  return apiFetch<CommentAiSuggestion[]>(
    `/api/v1/comments/${commentId}/ai-suggest`,
    { accessToken },
  )
}

/** Genera (o refina) una sugerencia. La IA corre como SSE (OT-0040):
 *  resuelve con el payload del evento `done`; `onEvent` recibe el progreso
 *  (pasos `retrieve`/`suggest`, `waiting`).
 *
 *  Errores relevantes (todos como `ApiError` con `code`):
 *    - 409 COMMENT_AI_COMMENT_RESOLVED: comment ya resuelto.
 *    - 409 COMMENT_AI_REFINEMENT_CAP: cadena en cap (3 refinements).
 *    - En el stream: TENANT_LLM_BALANCE_EXHAUSTED (saldo IA agotado),
 *      AI_PROVIDER_TIMEOUT / AI_PROVIDER_UNAVAILABLE (modelo),
 *      COMMENT_AI_AGENT_FAILED (el agente falló). */
export async function createCommentAiSuggestion(
  accessToken: string,
  commentId: string,
  body: CreateAiSuggestionBody,
  options?: { signal?: AbortSignal; onEvent?: (event: SseEvent) => void },
): Promise<CommentAiSuggestion> {
  return apiStreamResult<CommentAiSuggestion>(
    apiStream(`/api/v1/comments/${commentId}/ai-suggest`, {
      accessToken,
      body,
      signal: options?.signal,
      terminal: ['done', 'error'],
    }),
    options?.onEvent,
  )
}

/** Marca la sugerencia como aplicada. Idempotente — si ya estaba
 *  marcada, devuelve el row sin cambiar `applied_at` ni `applied_by`. */
export async function applyCommentAiSuggestion(
  accessToken: string,
  commentId: string,
  responseId: string,
): Promise<CommentAiSuggestion> {
  return apiFetch<CommentAiSuggestion>(
    `/api/v1/comments/${commentId}/ai-suggest/${responseId}/apply`,
    {
      accessToken,
      method: 'POST',
    },
  )
}

/** Doc 21 Fase 7.g — Cancelar UX. Descarta toda la cadena de propuestas
 *  no-aplicadas del comment. La próxima vez que el modal se abra, NO
 *  hará auto-resume — mostrará pantalla fresh.
 *
 *  Las rows permanecen en BD para audit + token accounting (los tokens
 *  ya se consumieron). Devuelve el count de propuestas descartadas. */
export async function discardCommentAiSuggestions(
  accessToken: string,
  commentId: string,
): Promise<{ discarded_count: number }> {
  return apiFetch<{ discarded_count: number }>(
    `/api/v1/comments/${commentId}/ai-suggest/discard`,
    {
      accessToken,
      method: 'POST',
    },
  )
}
